import { commonClient } from 'api';
import type {
  AddressSearchProvider,
  AddressToGeocode,
  CustomerAddress,
  GeocodedAddress,
  GeoCoordinate
} from 'api/models/mainservice';
import type { AddressLookupResultItemWithId } from 'components/AddressInput/models';
import { inputNameWithPrefx } from 'components/forms/_common';
import type { AddressInput } from 'components/forms/hook-form/address/AddressInput';
import type { SelectItemWithDescription } from 'components/forms/hook-form/SelectInput';
import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { handleApiErrorWithNotification } from './errorHandling';
import { DEFAULT_DECIMAL_PLACES } from './mathHelpers';
import { useCustomTranslation } from './translationHelper';
import { removeDiacritics, removeWhiteSpaces } from './validation';

export interface BasicAddress {
  city: string;
  street: string;
  postalCode: string;
  country?: string | null;
  coordinates?: GeoCoordinate | null;
}

export const oneLineAddress = (
  address?: { street: string; postalCode: string; city: string } | null
) => {
  return address ? `${address.street}, ${address.postalCode} ${address.city}` : '';
};

export const oneLineFullAddress = (
  address?: { street: string; city: string; postalCode: string; country: string } | null
) => {
  return address
    ? `${address.street}, ${address.postalCode} ${address.city}, ${address.country}`
    : '';
};

export const getSearchProviderTranslateKey = (provider: AddressSearchProvider) =>
  `search_provider_${provider}`;

export const getAddressToGeocode = (value?: AddressInput) => {
  if (value?.street && value.city && value.postalCode) {
    const addressToGeocode: AddressToGeocode = {
      street: value.street,
      city: value.city,
      postalCode: value.postalCode,
      country: value.country
    };

    return addressToGeocode;
  }
};

export const useAddressFormOperations = (prefix?: string) => {
  const { tError } = useCustomTranslation();
  const { setValue, getValues } = useFormContext();

  const addressValues = (prefix ? getValues(prefix) : getValues()) as AddressInput; // Cast to AddressInput

  const [isSearchingAddressAutoCompleteItems, setIsSearching] = useState(false);
  const [addressesResult, setAddressesResult] = useState<AddressLookupResultItemWithId[]>([]);
  const [addressSelectedSuccessfully, setAddressSelectedSuccessfully] = useState(false);

  const [originalLatitude, setOriginalLatitude] = useState(
    addressValues?.coordinates?.latitude || 0
  );
  const [originalLongitude, setOriginalLongitude] = useState(
    addressValues?.coordinates?.longitude || 0
  );

  const [geocodedAddress, setGeocodedAddress] = useState(getAddressToGeocode(addressValues));
  const [geocodeError, setGeocodeError] = useState<string | undefined>(undefined);

  const updateInputsValues = useCallback(
    (data: GeocodedAddress) => {
      setValue(inputNameWithPrefx('street', prefix), data.street, { shouldValidate: true });
      setValue(inputNameWithPrefx('city', prefix), data.city, { shouldValidate: true });
      setValue(inputNameWithPrefx('postalCode', prefix), data.postalCode, { shouldValidate: true });
      setValue(inputNameWithPrefx('country', prefix), data.country, { shouldValidate: true });
      setValue(inputNameWithPrefx('coordinates.latitude', prefix), data.latitude, {
        shouldValidate: true
      });
      setValue(inputNameWithPrefx('coordinates.longitude', prefix), data.longitude, {
        shouldValidate: true
      });

      setOriginalLatitude(data.latitude);
      setOriginalLongitude(data.longitude);
    },
    [setValue, prefix]
  );

  const updateGeoCoordinates = useCallback(
    (coordinates: GeoCoordinate) => {
      setValue(
        inputNameWithPrefx('coordinates.latitude', prefix),
        parseFloat(coordinates.latitude.toFixed(DEFAULT_DECIMAL_PLACES)),
        {
          shouldValidate: true,
          shouldDirty: true
        }
      );
      setValue(
        inputNameWithPrefx('coordinates.longitude', prefix),
        parseFloat(coordinates.longitude.toFixed(DEFAULT_DECIMAL_PLACES)),
        {
          shouldValidate: true,
          shouldDirty: true
        }
      );
    },
    [setValue, prefix]
  );

  const cleanInputsValues = useCallback(() => {
    setValue(inputNameWithPrefx('street', prefix), undefined);
    setValue(inputNameWithPrefx('city', prefix), undefined);
    setValue(inputNameWithPrefx('postalCode', prefix), undefined);
    setValue(inputNameWithPrefx('country', prefix), undefined);
    setValue(inputNameWithPrefx('coordinates.latitude', prefix), undefined);
    setValue(inputNameWithPrefx('coordinates.longitude', prefix), undefined);
  }, [setValue, prefix]);

  const loadAddresses = useCallback(
    async (searchPhrase: string, provider: AddressSearchProvider, isPublic?: boolean) => {
      if (!searchPhrase.trim()) return;
      setIsSearching(true);

      try {
        const endpoint = isPublic
          ? commonClient.publicSearchForAddresses
          : commonClient.searchForAddresses;
        const result = await endpoint(searchPhrase, provider);

        setAddressesResult(result.data.results.map((x, i) => ({ ...x, id: i.toString() })));
      } catch (e) {
        handleApiErrorWithNotification(e);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  const handleSelect = useCallback(
    async (
      addressId: string,
      provider: AddressSearchProvider,
      isPublic?: boolean,
      updateFakeStreet?: boolean,
      handleShowAllFields?: () => void
    ) => {
      const selectedAddress = addressesResult.find((x) => x.id === addressId);

      if (provider === 'Seznam' && selectedAddress?.details) {
        setAddressSelectedSuccessfully(true);
        if (updateFakeStreet && !selectedAddress.details.isInacurateResult)
          setValue('fakeStreet', oneLineAddress(selectedAddress.details), {
            shouldValidate: true,
            shouldDirty: true
          });

        if (handleShowAllFields && selectedAddress.details.isInacurateResult) handleShowAllFields();

        updateInputsValues(selectedAddress.details);
      }

      if (provider === 'Google' && selectedAddress?.placeId) {
        const endpoint = isPublic ? commonClient.publicGeocodePlaceId : commonClient.geocodePlaceId;

        const result = await endpoint(selectedAddress.placeId, provider);

        if (result.data.geocodedAddress) {
          setAddressSelectedSuccessfully(true);
          if (updateFakeStreet && !result.data.geocodedAddress.isInacurateResult)
            setValue('fakeStreet', oneLineAddress(result.data.geocodedAddress), {
              shouldValidate: true,
              shouldDirty: true
            });

          if (handleShowAllFields && result.data.geocodedAddress.isInacurateResult)
            handleShowAllFields();

          updateInputsValues(result.data.geocodedAddress);
        }
      }
    },
    [addressesResult, updateInputsValues, setValue]
  );

  const geocodeAddress = useCallback(
    async (addressToGeocode?: AddressToGeocode, provider?: AddressSearchProvider) => {
      if (!addressToGeocode) return;

      setIsSearching(true);

      try {
        const {
          data: { geocodedAddress }
        } = await commonClient.geocodeAddress(addressToGeocode, provider);

        if (geocodedAddress) {
          updateGeoCoordinates(geocodedAddress);

          setGeocodedAddress(addressToGeocode);
          setOriginalLongitude(geocodedAddress.longitude);
          setOriginalLatitude(geocodedAddress.latitude);

          if (geocodedAddress.isInacurateResult)
            setGeocodeError(tError('address_geocode_not_accurate'));

          // handleSearchExisting?.();
        }
      } catch {
        setGeocodeError(tError('address_geocode_error'));
      } finally {
        setIsSearching(false);
      }
    },
    [updateGeoCoordinates, tError]
  );

  const itemsForAutocomplete: SelectItemWithDescription[] = useMemo(
    () =>
      addressesResult.map((x) => ({
        id: x.id,
        name: x.address,
        description: x.addressSecondLine
      })),
    [addressesResult]
  );

  const isAddressChanged = useMemo(
    () => geocodedAddress && !isEqual(geocodedAddress, getAddressToGeocode(addressValues)),
    [geocodedAddress, addressValues]
  );

  return {
    updateInputsValues,
    loadAddresses,
    handleSelect,
    isSearchingAddressAutoCompleteItems,
    itemsForAutocomplete,
    addressSelectedSuccessfully,
    cleanInputsValues,
    updateGeoCoordinates,
    originalLatitude,
    originalLongitude,
    geocodeAddress,
    geocodeError,
    isAddressChanged
  };
};

export const searchForExistingAddress = (data: BasicAddress, addresses: CustomerAddress[]) => {
  const strictMatch = addresses.filter(
    (x) =>
      removeDiacritics(x.city.toLowerCase()) === removeDiacritics(data.city.toLowerCase()) &&
      removeDiacritics(x.street.toLowerCase()) === removeDiacritics(data.street.toLowerCase()) &&
      removeDiacritics(removeWhiteSpaces(x.postalCode)) ===
        removeDiacritics(removeWhiteSpaces(data.postalCode))
  );

  if (strictMatch.length > 1) {
    return undefined;
  } else if (strictMatch.length === 1) {
    return strictMatch[0];
  }

  const someMatch = addresses.filter(
    (x) =>
      removeDiacritics(x.city.toLowerCase()) === removeDiacritics(data.city.toLowerCase()) &&
      removeDiacritics(removeWhiteSpaces(x.postalCode)) ===
        removeDiacritics(removeWhiteSpaces(data.postalCode))
  );

  if (someMatch.length > 1) {
    return undefined;
  } else if (someMatch.length === 1) {
    return someMatch[0];
  }

  return undefined;
};
